import { useCallback } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import PropTypes from 'prop-types'
import { motion } from 'framer-motion'
import Box from '@aura/components/box'
import Button from '@aura/components/button'
import Grid from '@aura/components/grid'
import Link from '@aura/components/link'
import Typography from '@aura/components/typography'
import { withStyles } from '@aura/components/styles'
import LanguageIcon from '@mui/icons-material/Language'
import language from '../../constants/languages'
import { isFrench } from '../../constants/app'
import { HEDGEX_LANDING } from '../../constants/routes'
import styles from './styles'


const AAGLanding = ({ classes }) => {
    const { t: translate, i18n } = useTranslation()
    const history = useHistory()

    const toggleLanguage = useCallback(() => {
        const newLang = isFrench(i18n.language) ? language.ENGLISH : language.FRENCH
        i18n.changeLanguage(newLang)
    }, [ i18n ])

    const handleLogin = useCallback(() => {
        history.push('/login')
    }, [ history ])

    return (
        <Box className={classes.root}>
            {/* Background decorative elements */}
            <Box component="img" src="/img/AAG/Vector-yellow.svg" alt="" className={classes.yellowVector} />
            <Box component="img" src="/img/AAG/Vector-blue.svg" alt="" className={classes.blueVector} />            
            {/* Header */}
            <Grid container className={classes.header} justifyContent="space-between" alignItems="center">
                {/* RBC Logo */}
                <Grid item className={classes.logoContainer}>
                    <img 
                        src={isFrench(i18n.language) 
                            ? '/img/logos/rbc/RBC_Capital_Markets_logo_fr.svg' 
                            : '/img/logos/rbc/RBC_Capital_Markets_logo_en.svg'} 
                        alt="RBC Capital Markets Logo" 
                        className={classes.rbcLogo}
                    />
                </Grid>
                
                {/* Language Switcher */}
                <Grid item>
                    <Button 
                        variant="text" 
                        className={classes.languageButton}
                        onClick={toggleLanguage}
                        disableRipple
                    >
                        <LanguageIcon className={classes.languageIcon} />
                        {isFrench(i18n.language) ? 'English' : 'Français'}
                    </Button>
                </Grid>
            </Grid>

            {/* Main Content */}
            <Grid container className={classes.mainContent} spacing={0} alignItems="flex-start">
                {/* Left Section - Welcome */}
                <Grid item className={classes.welcomeContainer}>
                    <Typography className={classes.welcomeText}>
                        {translate('aag.welcome')}
                    </Typography>
                        
                    {/* AAG Logo */}
                    <Box className={classes.aagLogoContainer}>
                        <Typography component="span" className={classes.aagNameLogo}>{translate('aag.logoName')}</Typography>
                    </Box>

                    {/* Login Button */}
                    <Button 
                        variant="contained" 
                        className={classes.loginButton}
                        onClick={handleLogin}
                        fullWidth
                    >
                        {translate('aag.login')}
                    </Button>

                    {/* Additional Links */}
                    <Box className={classes.linksContainer}>
                        <Typography className={classes.poweredByText}>
                            {translate('aag.poweredBy')}
                        </Typography>
                        <Typography className={classes.digitalMarketplaceText}>
                            {translate('aag.digitalMarketplace')}
                        </Typography>
                    </Box>

                    {/* Horizontal Divider */}
                    <Box className={classes.dividerLine} />

                    {/* Assistance Section */}
                    <Box className={classes.assistanceContainer}>
                        <Box className={classes.assistanceTitleWrapper}>
                            <Typography className={classes.assistanceTitle}>
                                {translate('aag.needAssistance')}
                            </Typography>
                        </Box>
                        <Box className={classes.assistanceContactWrapper}>
                            <Typography className={classes.assistanceBody}>
                                <span className={classes.contactLabel}>{translate('aag.emailLabel')}</span>{' '}
                                <a href="mailto:AlternativeAssets@rbccm.com" className={classes.contactValue}>AlternativeAssets@rbccm.com</a>
                            </Typography>
                            <Typography className={classes.assistanceBody}>
                                <span className={classes.contactLabel}>{translate('aag.telephoneLabel')}</span>{' '}
                                <a href="tel:+12128587200" className={classes.contactValue}>+ 1 212 858 7200</a>
                            </Typography>
                        </Box>
                    </Box>

                </Grid>
                {/* Glass Card - Text Content */}
                <Grid item className={classes.firstCardWrapper}>
                    <Box className={classes.glassCard}>
                        <Typography variant="h4" className={classes.cardTitle}>
                            {translate('aag.cardTitle')}
                        </Typography>
                        
                        <Typography variant="h7" className={classes.cardDescription}>
                            <Trans
                                i18nKey="aag.cardDescription"
                                components={{
                                    portfolioLink: (
                                        <Link
                                            className={classes.descriptionLink}
                                            href={HEDGEX_LANDING}
                                        />
                                    ),
                                    rbcLink: (
                                        <Link
                                            className={classes.descriptionLink}
                                            href="https://www.rbc.com/about-rbc.html"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        />
                                    )
                                }}
                            />
                        </Typography>
                    </Box>
                </Grid>
                {/* Second Glass Card - Preview Section */}
                <Grid item className={classes.secondCardWrapper}>
                    <Box className={classes.secondCardGlassCard}>
                        <Box className={classes.previewContainer}>
                            <motion.div
                                initial={{ opacity: 0, x: 50 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.6, duration: 0.8, ease: 'easeOut' }}
                                className={classes.previewMotionWrapper}
                            >
                                <Box
                                    component='img'
                                    src='/img/AAG/aag-laptop-preview.svg'
                                    alt='Laptop Preview'
                                    className={classes.previewDesktop}
                                />
                            </motion.div>
                            <Box
                                component='img'
                                src='/img/AAG/aag-laptop-preview.png'
                                alt='Laptop Preview'
                                className={classes.previewMobile}
                            />
                        </Box>
                    </Box>
                </Grid>
                <Grid className={classes.taglineWrapper}>
                    <Typography className={classes.taglineText}>
                        {translate('aag.taglineLine1')}
                    </Typography>
                    <Typography className={classes.taglineText}>
                        {translate('aag.taglineLine2')}
                    </Typography>
                    <Typography className={classes.taglineText}>
                        {translate('aag.taglineLine3')}
                    </Typography>   
                </Grid>
            </Grid>

            {/* Footer */}
            <Grid container className={classes.footer} justifyContent="space-between" alignItems="center">
                <Grid item>
                    <Typography className={classes.footerCopyright}>
                        {translate('aag.copyright')}
                    </Typography>
                </Grid>
                <Grid item>
                    <Link
                        className={classes.footerPrivacy}
                        href="https://www.rbc.com/privacysecurity/ca/index.html"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {translate('aag.privacy')}
                    </Link>
                </Grid>
            </Grid>

        </Box>
    )
}

AAGLanding.propTypes = {
    classes: PropTypes.object.isRequired
}

export default withStyles(AAGLanding, styles)
